<template>
  <div
    class="segmented-button"
    :class="classes"
  >
    <button
      v-for="segment in segments"
      :key="segment.text"
      class="segment outline"
      type="button"
      :class="{ 'segment--active': segment.active }"
      @click="handleSegmentClick(segment)"
    >
      <the-icon
        v-if="segment.active"
        art="solid"
        class="selection__icon"
        name="check"
      />
      <span
        v-sanitize-html="segment.text"
        class="title-3"
      />
    </button>
  </div>
</template>

<script setup>
import { reactive, watch } from 'vue'

import { ReactivityUtil } from '@/utils/Reactivity'
import { useResponsiveSize } from '@/hooks/useResponsiveSize'

// INIT
const emit = defineEmits(['change'])

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
})

// HOOKS
const { getSizeClasses } = useResponsiveSize()

// DATA
const segments = reactive([])

// COMPUTED
const classes = getSizeClasses('segmented-button')

// METHODS
function handleSegmentClick(selectedSegment) {
  ReactivityUtil.reAssign(
    segments,
    segments.map(segment => {
      return {
        ...segment,
        active: segment.text === selectedSegment.text,
      }
    })
  )

  emit('change', segments.find(segment => segment.active).value)
}

// WATCHER
watch(
  () => props.items,
  val => {
    ReactivityUtil.reAssign(segments, val)
  },
  { immediate: true }
)
</script>

<style scoped>
.segmented-button {
  --seg-btn-c-text: var(--Interaction-States-Chips-Positives-on-unselected-enabled-positive);
  --seg-btn-c-bg: var(--Interaction-States-Chips-Positives-unselected-enabled-positive);
  --seg-btn-c-border: var(--outline-variant);

  display: flex;
  flex-direction: column;
  margin: 1px 0 0 1px;
  width: 100%;

  &--sm {
    --seg-btn-padding: 10px var(--fixed-spacing-fix-03);
  }

  &--md {
    --seg-btn-padding: 12px var(--fixed-spacing-fix-03);
  }

  &--lg {
    --seg-btn-padding: 14px var(--fixed-spacing-fix-03);
  }

  .segment {
    padding: var(--seg-btn-padding);
    background-color: var(--seg-btn-c-bg);
    color: var(--seg-btn-c-text);
    border: 1px solid var(--seg-btn-c-border);
    margin: 0 0 -1px 0;
    cursor: pointer;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;

    &--active {
      --seg-btn-c-text: var(--Interaction-States-Chips-Positives-on-selected-enabled-positive);
      --seg-btn-c-bg: var(--Interaction-States-Chips-Positives-selected-enabled-positive);
      --seg-btn-c-border: var(--secondary);
      z-index: var(--pet-stack-level-element);
      pointer-events: none;
    }

    &:first-child {
      border-top-left-radius: var(--fixed-border-radius-fix-04);
      border-top-right-radius: var(--fixed-border-radius-fix-04);
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    &:last-child {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      border-bottom-left-radius: var(--fixed-border-radius-fix-04);
      border-bottom-right-radius: var(--fixed-border-radius-fix-04);
    }
  }

  .selection__icon {
    font-size: 16px;
    color: var(--secondary);
    margin-right: 4px;
    order: 1;
  }

  .segment span {
    order: 2;
    padding-left: 0;
  }

  &--error .segment {
    border-color: var(--error);
  }

  .segment.outline:focus-visible {
    outline: 2px solid var(--on-surface);
    outline-offset: var(--fixed-spacing-fix-01);
    border-radius: var(--fixed-border-radius-fix-02);
    z-index: calc(var(--pet-stack-level-element) + 1);
  }

  /* Mobile vertical layout focus styles */
  &:has(.segment:first-child:focus-visible) .segment:first-child:focus-visible {
    border-radius: var(--fixed-border-radius-fix-04) var(--fixed-border-radius-fix-04) 0 0;
  }

  &:has(.segment:last-child:focus-visible) .segment:last-child:focus-visible {
    border-radius: 0 0 var(--fixed-border-radius-fix-04) var(--fixed-border-radius-fix-04);
  }

  &:has(.segment:not(:first-child):not(:last-child):focus-visible) .segment:not(:first-child):not(:last-child):focus-visible {
    border-radius: 0;
  }

  /* Desktop horizontal layout */
  @media (--v-medium) {
    flex-direction: row;
    margin: 1px 0 0 1px;

    .segment {
      margin: -1px 0 0 -1px;
      text-align: center;
      justify-content: center;
      gap: 8px;

      &:first-child {
        border-top-left-radius: var(--fixed-border-radius-fix-07);
        border-bottom-left-radius: var(--fixed-border-radius-fix-07);
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }

      &:last-child {
        border-top-right-radius: var(--fixed-border-radius-fix-07);
        border-bottom-right-radius: var(--fixed-border-radius-fix-07);
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }

    .selection__icon {
      margin-right: 8px;
    }

    /* Desktop focus styles */
    &:has(.segment:first-child:focus-visible) .segment:first-child:focus-visible {
      border-radius: var(--fixed-border-radius-fix-07) 0 0 var(--fixed-border-radius-fix-07);
    }

    &:has(.segment:last-child:focus-visible) .segment:last-child:focus-visible {
      border-radius: 0 var(--fixed-border-radius-fix-07) var(--fixed-border-radius-fix-07) 0;
    }

    &:has(.segment:not(:first-child):not(:last-child):focus-visible) .segment:not(:first-child):not(:last-child):focus-visible {
      border-radius: 0;
    }
  }
}
</style>
